import { TriangleArrowDownIcon } from '@jouzen/ecom-components/icons/TriangleArrowDownIcon';
import { Button } from '@jouzen/ecom-components/v3/Button';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { forwardRef, type MouseEventHandler, type Ref } from 'react';

import { EventType } from '@/analytics/types';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';
import SectionHeading from '@/app/components/SectionHeading';

const PopPostHero = forwardRef((_, ref: Ref<HTMLDivElement>) => {
  const t = useTranslations();
  const path = usePathname();

  const handleCtaClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
    event.preventDefault();

    void window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: 'see_tech_specs',
      location: 'gen4 pop',
      path,
    });

    const { href } = event.currentTarget;
    const { hash } = new URL(href);
    const scrollTarget = document.querySelector(hash);
    scrollTarget?.scrollIntoView();
  };

  return (
    <div
      className="pt-20 pb-5 lg:pb-0 lg:nojs:pb-20"
      data-cy="pop-post-hero-section"
      ref={ref}
    >
      <LayoutGrid>
        <GridItem colEnd={{ sm: 'main' }} colStart={{ sm: 'main' }}>
          <div className="flex flex-col items-center">
            <SectionHeading
              animated
              className="mb-6"
              content="pop_gen4_posthero_title"
              emProps={{
                className: 'bg-clip-text font-light text-transparent pr-[2px]',
                style: {
                  backgroundImage: 'inherit',
                },
              }}
              typographyProps={{
                align: 'center',
                className:
                  'bg-clip-text font-normal text-transparent leading-tighter max-w-[7.5em]',
                Element: 'h2',
                style: {
                  backgroundImage:
                    'linear-gradient(141.72deg, #1E2427 -0.36%, #AF751B 87.58%)',
                },
                variant: 'h1',
              }}
            />
            <Motion
              className="mb-8 max-w-[570px]"
              initial={{ opacity: 0 }}
              transition={{ delay: 0.35, duration: 1, type: 'tween' }}
              viewport={{ once: true }}
              whileInView={{ opacity: 1 }}
            >
              <Typography align="center">
                <RichText messageKey="pop_gen4_posthero_copy" />
              </Typography>
            </Motion>
            <Motion
              initial={{ opacity: 0 }}
              transition={{ delay: 0.5, duration: 1, type: 'tween' }}
              viewport={{ once: true }}
              whileInView={{ opacity: 1 }}
            >
              <Button
                data-cy="pop-post-hero-cta"
                href="#pdp-tech-specs"
                icon={<TriangleArrowDownIcon height={10} />}
                onClick={handleCtaClick}
                variant="outlined-dark"
              >
                {t('pop_gen4_posthero_cta')}
              </Button>
            </Motion>
          </div>
        </GridItem>
      </LayoutGrid>
    </div>
  );
});

PopPostHero.displayName = 'PopPostHero';

export default PopPostHero;
